import {
  Event,
  EventInput,
  LocationType,
  Shift,
  ShiftInput,
  Visibility,
  WaiverRequirement,
} from '@amzn/red-velvet-api';
import { OptionDefinition } from '@amzn/awsui-components-react/polaris/internal/components/option/interfaces';
import { SelectProps } from '@amzn/awsui-components-react';

export const EVENT_DATE_DISPLAY_WITH_MONTH_DAY_OPTIONS: Intl.DateTimeFormatOptions = {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
};

export function formatCharacterCount(currentLength: number, maxLength: number): string {
  return `${currentLength}/${maxLength}`;
}

export function generateEventStartDateWithMonthAndDayToDisplay(
  selectedTimezone: string | null,
  language: string,
  event: Event
) {
  return event.startTimestamp?.toLocaleDateString(language, {
    ...EVENT_DATE_DISPLAY_WITH_MONTH_DAY_OPTIONS,
    timeZone: selectedTimezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
  });
}

export function generateEventEndDateWithMonthAndDayToDisplay(
  selectedTimezone: string | null,
  language: string,
  event: Event
) {
  return event.endTimestamp?.toLocaleDateString(language, {
    ...EVENT_DATE_DISPLAY_WITH_MONTH_DAY_OPTIONS,
    timeZone: selectedTimezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
  });
}

export function isSameDayFullDay(start: Date, end: Date): boolean {
  const startDate = new Date(start);
  const endDate = new Date(end);

  const isSameDay =
    startDate.getFullYear() === endDate.getFullYear() &&
    startDate.getMonth() === endDate.getMonth() &&
    startDate.getDate() === endDate.getDate();
  const isFullDay =
    startDate.getHours() === 0 &&
    startDate.getMinutes() === 0 &&
    startDate.getSeconds() === 0 &&
    endDate.getHours() === 23 &&
    endDate.getMinutes() === 59 &&
    endDate.getSeconds() === 0;

  // Also check UTC for backward compatibility with existing events stored in UTC
  const isSameDayUTC =
    startDate.getUTCFullYear() === endDate.getUTCFullYear() &&
    startDate.getUTCMonth() === endDate.getUTCMonth() &&
    startDate.getUTCDate() === endDate.getUTCDate();
  const isFullDayUTC =
    startDate.getUTCHours() === 0 &&
    startDate.getUTCMinutes() === 0 &&
    startDate.getUTCSeconds() === 0 &&
    endDate.getUTCHours() === 23 &&
    endDate.getUTCMinutes() === 59 &&
    endDate.getUTCSeconds() === 0;

  return (isSameDay && isFullDay) || (isSameDayUTC && isFullDayUTC);
}

export function isUnlistedEvent(event: Event): boolean {
  return (
    event.source === 'VC' &&
    !!event.startTimestamp &&
    !!event.endTimestamp &&
    isSameDayFullDay(event.startTimestamp, event.endTimestamp) &&
    event.status !== 'DRAFT' // avoid confusion with listed events in draft
  );
}

/**
 * CCE-7232: Returns true when an event should be blocked from coordinator management
 * (Track Attendance, Manage Signups) because it shows on the coordinator Drafts tab.
 *
 * Block when showEventOnVCDraft is NOT explicitly false:
 * - true -> block (event is on the Drafts tab for coordinators)
 * - undefined/absent -> block (no ownership info; safe default, matches today's behavior)
 * - false -> allow (event is on Past/Upcoming for coordinators, even if unpublished/archived)
 *
 * PUBLISHED and unlisted events are never blocked.
 */
export function shouldBlockAsVCDraft(event: Event | undefined): boolean {
  if (!event) return false;
  if (event.status === 'PUBLISHED') return false;
  if (isUnlistedEvent(event)) return false;
  return event.showEventOnVCDraft !== false;
}

export function findOption(options: SelectProps.Options, value: string): OptionDefinition | null {
  for (const opt of options) {
    if ('value' in opt && opt.value === value) return opt;
    if ('options' in opt) {
      const found = opt.options?.find((o) => o.value === value);
      if (found) return found;
    }
  }
  return null;
}

export function mapEventToEventInput(event: Event): EventInput {
  // Derive locationType from the event's location object.
  // The backend now returns locationType on location (ON_SITE, OFF_SITE, or VIRTUAL).
  // For backward compat with older events, fall back to deriving from amazonBuilding.
  const locType = event.location?.locationType;
  const resolvedLocationType: LocationType =
    locType === LocationType.VIRTUAL ||
    locType === LocationType.ON_SITE ||
    locType === LocationType.OFF_SITE
      ? locType
      : !event.location || (!event.location.name && !event.location.city && !event.location.street)
        ? LocationType.VIRTUAL
        : event.location.amazonBuilding
          ? LocationType.ON_SITE
          : LocationType.OFF_SITE;

  // The location object in DynamoDB may be stale from a previous locationType.
  // Discard only when there's a clear mismatch: ON_SITE data in OFF_SITE type or vice versa.
  const hasOnCampusData = !!event.location?.amazonBuilding;
  const locationMatchesType =
    resolvedLocationType === LocationType.ON_SITE ? true : !hasOnCampusData;

  const location =
    resolvedLocationType === LocationType.VIRTUAL
      ? { locationType: LocationType.VIRTUAL }
      : !locationMatchesType
        ? undefined
        : {
            ...event.location,
            locationType: resolvedLocationType,
            // Keep the stored address (carries the house number); rebuild only when absent (GIVEHUB).
            address_string:
              event.location?.address_string ||
              [
                event.location?.street,
                event.location?.city,
                event.location?.province,
                event.location?.postal_code,
                event.location?.country,
              ]
                .filter(Boolean)
                .join(', '),
          };

  return {
    title: event.title,
    description: event.description,
    shortDescription: event.shortDescription,
    location,
    locationVirtualNotes:
      event.locationVirtualNotes ??
      (resolvedLocationType === LocationType.VIRTUAL ? event.location?.notes : undefined),
    timeZone: event.timeZone,
    focusArea: event.focusArea,
    primaryActivity: event.primaryActivity,
    usedAciGrant: event.usedAciGrant,
    grantId: event.grantId,
    additionalCoordinators: event.additionalCoordinators,
    externalPlanningPartner: event.externalPlanningPartner,
    otherPartner: event.otherPartner,
    elgAffiliations: event.elgAffiliations,
    eventType: event.eventType,
    allowGuests: event.allowGuests,
    waiverUrl: event.waiver?.url,
    waiverRequired: (event.waiver?.acceptance as WaiverRequirement) ?? WaiverRequirement.NONE,
    hasTShirt: event.hasTShirt,
    signupDeadline: event.signupDeadline,
    questions: event.questions,
    amazonContacts: event.amazonContacts,
    externalContacts: event.externalContacts,
    communityOrganizations: event.communityOrganizations,
    heroImageUrl: event.heroImageUri,
    visibility: (event.visibility as Visibility) ?? Visibility.PUBLIC,
    notifyCoordinatorOnSignup: event.notifyCoordinatorOnSignup,
    signupSummaryFrequency: event.signupSummaryFrequency,
    signupSummaryWeeklyDays: event.signupSummaryWeeklyDays,
    realTimeNotificationRecipients: event.realTimeNotificationRecipients,
    signupSummaryRecipients: event.signupSummaryRecipients,
  };
}

/**
 * Maps a Shift (from API) to ShiftInput (for form state).
 * Dates are cast rather than defaulted — if the backend returns undefined,
 * the date picker will render empty and validateShifts() will block submission.
 */
export function mapShiftToShiftInput(shift: Shift): ShiftInput {
  return {
    shiftId: shift.shiftId,
    name: shift.name,
    startDateTime: shift.startDateTime as Date,
    endDateTime: shift.endDateTime as Date,
    maxSignup: shift.maxSignup,
    useEventLocation: shift.useEventLocation,
    location: shift.location,
    shiftTimingType: shift.shiftTimingType,
    shiftLength: shift.shiftLength,
    waitlistEnabled: shift.waitlistEnabled,
  };
}

export const EDIT_EVENT_STEP_PARAM = 'step';

/** Number of steps in the edit wizard. */
export const EDIT_EVENT_WIZARD_STEP_COUNT = 4;

/** Zero-based index of the Team & Partnership step. */
export const EDIT_EVENT_NOTIFICATIONS_STEP_INDEX = 2;

/**
 * Also declared in the notification Lambda, which builds the emailed link. Rename one side and every
 * link already sitting in a sent email lands on the wrong step.
 */
export const EDIT_EVENT_NOTIFICATIONS_STEP_NAME = 'notifications';

/**
 * Step names accepted on `?step=`. Emailed links use a name rather than a number so they keep
 * working if the wizard's steps are reordered after the email was sent.
 *
 * A Map, not an object literal, so a hand-crafted `?step=constructor` cannot resolve to an
 * inherited member and break the wizard.
 */
const EDIT_EVENT_DEEP_LINK_STEP_INDEX = new Map<string, number>([
  [EDIT_EVENT_NOTIFICATIONS_STEP_NAME, EDIT_EVENT_NOTIFICATIONS_STEP_INDEX],
]);

/** Resolves a 1-based `?step=N` to its zero-based index, rejecting anything but plain digits. */
function resolveNumericEditEventStep(step: string): number | undefined {
  if (!/^\d+$/.test(step)) {
    return undefined;
  }
  const oneBasedStep = Number(step);
  if (oneBasedStep < 1 || oneBasedStep > EDIT_EVENT_WIZARD_STEP_COUNT) {
    return undefined;
  }
  return oneBasedStep - 1;
}

/**
 * Resolves a `?step=` value to a zero-based wizard step index, accepting a step name or a 1-based
 * step number. `undefined` means the link points at no step this build knows, and the caller opens
 * the first step instead.
 */
export function resolveEditEventStepIndex(step: string | null | undefined): number | undefined {
  if (!step) {
    return undefined;
  }
  return (
    EDIT_EVENT_DEEP_LINK_STEP_INDEX.get(step.toLowerCase()) ?? resolveNumericEditEventStep(step)
  );
}

/** Keeps arbitrary text out of the analytics metric when a link carries a junk step value. */
export function describeEditEventStepForMetric(step: string): string {
  return /^[a-z0-9-]{1,32}$/i.test(step) ? step : 'malformed';
}

/** Lets the route highlight the section on arrival only, and not on later visits to the step. */
export interface NotificationsHighlight {
  active: boolean;
  onShown: () => void;
}

/**
 * Whether a `?step=` value asks for the notification preferences by name. A numeric `?step=3` opens
 * the same step without pulling the coordinator's attention to one section of it.
 */
export function isEditEventNotificationsDeepLink(step: string | null | undefined): boolean {
  return !!step && step.toLowerCase() === EDIT_EVENT_NOTIFICATIONS_STEP_NAME;
}
